import {makeObservable, observable, action} from 'mobx';


export type ScreenSize = 'mobile' | 'tablet' | 'desktop';


export const DESKTOP_MIN_WIDTH = 1250;
export const TABLET_MIN_WIDTH = 480;


export class ScreenSizeProvider {
    view: ScreenSize;
    size: ResizeObserverSize;
    isThrottling: boolean = false;
    private pollIntervalId: ReturnType<typeof setInterval> | undefined;

    constructor() {
        // Initialize fields before making them observable
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
            this.size = {
                inlineSize: window.innerWidth,
                blockSize: window.innerHeight,
            };
        } else {
            // Default values for server-side rendering or tests
            this.size = {
                inlineSize: 1920, // Default desktop width
                blockSize: 1080,  // Default desktop height
            };
        }
        
        this.view = this.getView(this.size);

        // Now make them observable after initialization
        makeObservable(this, {
            size: observable,
            view: observable,
            setSize: action,
            setView: action,
        });

        // Set up event listener only in browser
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.throttledResizeHandler);
            // Confirmed by hand: when this window is an iframe and its PARENT
            // resizes it purely via the element's CSS width/height (e.g. the
            // demo's device-preview frame swapping breakpoints without
            // remounting), window.innerWidth updates correctly but neither the
            // 'resize' event, a ResizeObserver on documentElement, nor a
            // matchMedia 'change' listener ever fires - so nothing here calls
            // us back on its own. A cheap poll is the only mechanism that
            // reliably catches that case; the resize listener above still
            // makes a real window resize feel instant.
            this.pollIntervalId = setInterval(this.throttledResizeHandler, 300);
        }
        this.setDynamicHeight();
    }

    // Throttled resize handler to limit frequency of resize events. Uses
    // setTimeout rather than requestAnimationFrame deliberately - rAF only
    // ever fires once the page is actually being painted, so a backgrounded/
    // unpainted tab (or an automation-driven browser with nothing rendering
    // it) would never clear isThrottling, permanently wedging this handler.
    throttledResizeHandler = () => {
        if (typeof window === 'undefined') return;

        if (!this.isThrottling) {
            this.isThrottling = true;
            setTimeout(() => {
                this.setDynamicHeight();
                this.isThrottling = false;
            }, 16);
        }
    };

    // Clean up event listener when this provider is no longer needed
    destroy = () => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.throttledResizeHandler);
        }
        if (this.pollIntervalId !== undefined) clearInterval(this.pollIntervalId);
    };

    setDynamicHeight = () => {
        if (typeof window === 'undefined') return;
        
        try {
            const newSize = {
                inlineSize: window.innerWidth,
                blockSize: window.innerHeight,
            };

            // Only update if the size has actually changed
            if (newSize.inlineSize !== this.size.inlineSize || newSize.blockSize !== this.size.blockSize) {
                this.setSize(newSize);
                this.setView(this.getView(newSize));
            }

            // Update the CSS custom property for --vh
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        } catch (error) {
            // Handle potential errors
        }
    }

    setSize = (size: ResizeObserverSize) => {
        this.size = size;
    }

    setView = (view: ScreenSize) => {
        this.view = view;
    }

    getView = (size: ResizeObserverSize): ScreenSize => {
        if (size.inlineSize >= DESKTOP_MIN_WIDTH) {
            return 'desktop';
        } else if (size.inlineSize > TABLET_MIN_WIDTH) {
            return 'tablet';
        }
        return 'mobile';
    }
}

export const screenSizeProvider = new ScreenSizeProvider();
